import React from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const StyledCard = styled.div({
    width: "100%",
    minHeight: "62px",
    padding: tokens.globals["spacing-3-5"],

    background: tokens.colors.surfaces["container-background"],

    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    borderRadius: tokens.globals["border-radius"],

    boxShadow: tokens.effects.effects.shadow.default,

    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: tokens.globals["spacing-2-5"],

    boxSizing: "border-box",
});

const StyledTopRow = styled.div({
    width: "100%",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    gap: tokens.globals["spacing-6"],
});

const StyledStatus = styled.span({
    minWidth: 0,
    flex: 1,

    color: tokens.colors.text.primary,

    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.bold,
    lineHeight: 1.1,

    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

const StyledCancel = styled.button({
    minHeight: tokens.globals["spacing-5"],

    padding: 0,
    border: 0,

    background: "transparent",

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.globals["spacing-0-5"],

    color: tokens.colors.ui.destructive,

    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: 1.1,

    whiteSpace: "nowrap",

    cursor: "pointer",

    i: {
        width: tokens.globals["spacing-4"],
        height: tokens.globals["spacing-4"],

        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",

        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "14px",
    },

    "&:hover:not(:disabled)": {
        opacity: 0.8,
    },

    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-0-5"],
        borderRadius: tokens.globals["border-radius"],
    },

    "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
    },
});

const StyledProgress = styled.div({
    width: "100%",
    height: tokens.globals["spacing-2"],

    position: "relative",

    overflow: "hidden",

    borderRadius: "9999px",

    background: tokens.colors.surfaces["border-divider"],
});

const StyledProgressBar = styled.div(({ $percent }) => ({
    width: `${$percent}%`,
    height: "100%",

    borderRadius: "9999px",

    background: tokens.colors.brand.blue["500"],

    transition: "width 0.2s ease-in-out",
}));

/**
 * Progress bar card for long-running/bulk operations (e.g. bulk lock/unlock,
 * bulk version assignment), with an optional cancel action.
 * @param {string} props.label Verb/label shown before the count (e.g. "Locking").
 * @param {number} props.current Number of items completed so far; clamped to >= 0.
 * @param {number} props.total Total number of items; 0 renders 0% instead of dividing by zero.
 * @param {string} [props.unit] Unit label shown after the count.
 * @param {() => void} [props.onCancel] Shows a "Cancel remaining" action when provided.
 * @param {boolean} [props.cancelDisabled] Disables the cancel button.
 */
function ProgressCard({
    label,
    current,
    total,
    unit = "Programmes",
    onCancel,
    cancelText = "Cancel remaining",
    cancelDisabled = false,
}) {
    const safeTotal = Number(total) || 0;
    const safeCurrent = Math.max(0, Number(current) || 0);

    const percent =
        safeTotal > 0
            ? Math.min(100, (safeCurrent / safeTotal) * 100)
            : 0;

    return (
        <StyledCard
            role="status"
            aria-live="polite"
            aria-label={`${label} ${safeCurrent} of ${safeTotal} ${unit}`}
        >
            <StyledTopRow>
                <StyledStatus>
                    {label}... {safeCurrent} of {safeTotal} {unit}
                </StyledStatus>

                {onCancel && (
                    <StyledCancel
                        type="button"
                        onClick={onCancel}
                        disabled={cancelDisabled}
                    >
                        <i
                            className="fal fa-times-circle"
                            aria-hidden="true"
                        />

                        {cancelText}
                    </StyledCancel>
                )}
            </StyledTopRow>

            <StyledProgress
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax={safeTotal}
                aria-valuenow={safeCurrent}
                aria-valuetext={`${safeCurrent} of ${safeTotal} ${unit}`}
            >
                <StyledProgressBar $percent={percent} />
            </StyledProgress>
        </StyledCard>
    );
}

ProgressCard.displayName = "ProgressCard";

export default ProgressCard;
